/*───────────────────────────────────────────────────────────────────────────*\
  File      : src/utils/featureFlags.ts
  Purpose   : Centralized runtime feature-flag checks. Every flag defaults
              to OFF (false) unless explicitly set to "true" in the env.
              Server-side and client-side flags are separated clearly.
\*───────────────────────────────────────────────────────────────────────────*/

/*==========================================================================*
 *                       SERVER-SIDE FLAGS                                  *
 *==========================================================================*/

/** Master switch for user authentication. When false, all auth is disabled. */
export function isUserAuthEnabled(): boolean {
  return process.env.ENABLE_USER_AUTH === "true";
};

/** Google OAuth sub-flag (requires ENABLE_USER_AUTH=true to matter). */
export function isGoogleOAuthEnabled(): boolean {
  return isUserAuthEnabled() && process.env.ENABLE_GOOGLE_OAUTH === "true";
};

/** Magic-link sub-flag (requires ENABLE_USER_AUTH=true to matter). */
export function isMagicLinkEnabled(): boolean {
  return isUserAuthEnabled() && process.env.ENABLE_MAGIC_LINK === "true";
};

/** AI Aggregator switch. When false, aggregator facade returns not-enabled. */
export function isAIAggregatorEnabled(): boolean {
  return process.env.ENABLE_AI_AGGREGATOR === "true";
};

/*==========================================================================*
 *                       CLIENT-SIDE FLAGS                                  *
 *  Read via NEXT_PUBLIC_* env vars (available in browser at build time).   *
 *==========================================================================*/

export function isUserAuthEnabledClient(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_USER_AUTH === "true";
};

export function isGoogleOAuthEnabledClient(): boolean {
  return isUserAuthEnabledClient() && process.env.NEXT_PUBLIC_ENABLE_GOOGLE_OAUTH === "true";
};

export function isMagicLinkEnabledClient(): boolean {
  return isUserAuthEnabledClient() && process.env.NEXT_PUBLIC_ENABLE_MAGIC_LINK === "true";
};

export function isAIAggregatorEnabledClient(): boolean {
  return process.env.NEXT_PUBLIC_ENABLE_AI_AGGREGATOR === "true";
};

/*==========================================================================*
 *                       DISABLED-RESPONSE HELPER                           *
 *==========================================================================*/

/**
 * Standard response returned by facades when a feature is disabled.
 * UI code can check `response.disabled === true` to show appropriate messaging.
 */
export interface DisabledFeatureResponse {
  success: false;
  disabled: true;
  error: string;
};

export function featureDisabledResponse(featureName: string): DisabledFeatureResponse {
  return {
    success: false,
    disabled: true,
    error: `${featureName} is not enabled. Set the appropriate ENABLE_* flag in .env to activate.`,
  };
};

/*──────────────────────────── End of file ────────────────────────────────*/
